import { useCallback, useRef, useMemo, useEffect } from "react";
import useApi from "@rapid/hooks/useApi";
import { useCurrentMedia, useImproveBatch, useLimitedSources, useStore } from "@rapid/store";
import type { Annotation } from "@rapid/types/annotation";
import {
    AiTools,
    type AiToolUsed,
    type AnnotationBox,
    type AnnotationData
} from "@rapid/utils/api/datasets";
import { annotationSyncQueue, type SyncRequest } from "@rapid/services/annotationSyncQueue";

// Generate a unique tab ID for this browser tab (for concurrent edit tracking)
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

/**
 * Convert app Annotation format to API AnnotationBox format
 */
function annotationToBox(annotation: Annotation): AnnotationBox {
    return {
        id: annotation.id,
        label: annotation.label,
        x: annotation.x,
        y: annotation.y,
        width: annotation.width,
        height: annotation.height,
        confidence: annotation.confidence ?? null,
        type: annotation.type,
        // Include points if present (for polygons/segmentation)
        ...(annotation.masks ? {} : {}) // masks are handled separately
    };
}

/**
 * Build the annotation data structure expected by the updateAnnotation endpoint
 */
function buildAnnotationData(
    annotations: Annotation[],
    imageWidth: number,
    imageHeight: number
): AnnotationData {
    return {
        // The key field must be the literal string "annotation" to match the core app format
        key: "annotation",
        width: imageWidth,
        height: imageHeight,
        boxes: annotations.map(annotationToBox)
    };
}

/**
 * Calculate the next annotation ID based on existing annotations
 */
function getNextAnnotationId(annotations: Annotation[]): string {
    const numericIds = annotations.map((a) => parseInt(a.id, 10)).filter((id) => !isNaN(id));

    const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
    return String(maxId + 1);
}

export interface UseAnnotationSyncOptions {
    /** Enable debug logging */
    debug?: boolean;
}

export interface AnnotationSyncResult {
    /** Sync annotations to backend immediately */
    syncAnnotations: (
        annotations: Annotation[],
        options?: { aiToolUsed?: AiToolUsed; modelAssisted?: boolean }
    ) => Promise<void>;
    /** Queue annotations for sync (debounced, persists across image changes) */
    queueSync: (annotations: Annotation[]) => void;
    /** Whether a sync is currently in progress */
    isSyncing: boolean;
    /** Last sync error, if any */
    lastError: Error | null;
    /** Version number from last successful sync */
    lastVersion: number | null;
    /** Set pending AI tool (will be used on next sync then cleared) */
    setPendingAiTool: (tool: AiToolUsed) => void;
    /** Clear pending AI tool */
    clearPendingAiTool: () => void;
}

/**
 * Hook to sync annotations to the backend for the current improve image.
 *
 * Usage:
 * ```tsx
 * const { syncAnnotations, isSyncing, lastError, setPendingAiTool } = useAnnotationSync();
 *
 * // After user edits annotations:
 * await syncAnnotations(currentAnnotations);
 *
 * // After AI inference (OWLv2):
 * await syncAnnotations(annotations, { aiToolUsed: AiTools.BOX_PROMPTING });
 *
 * // After AI inference (SAM2):
 * await syncAnnotations(annotations, { aiToolUsed: AiTools.TEXT_PROMPTING });
 *
 * // Or set pending AI tool before annotation changes (for debounced syncs):
 * setPendingAiTool(AiTools.BOX_PROMPTING);
 * // ... annotations update, debounced sync will use the pending tool
 * ```
 */
export function useAnnotationSync(options: UseAnnotationSyncOptions = {}): AnnotationSyncResult {
    const { debug = false } = options;
    const api = useApi();
    const improveBatch = useImproveBatch();
    const sources = useLimitedSources();
    const currentMedia = useCurrentMedia();

    // Note: We read pendingAiTool directly from the store at sync time (not via hook)
    // to avoid recreating syncAnnotations when the pending tool changes.
    // This prevents debounced syncs from being cancelled when setPendingAiTool is called.

    // Track sync state
    const isSyncingRef = useRef(false);
    const lastErrorRef = useRef<Error | null>(null);
    const lastVersionRef = useRef<number | null>(null);

    // Get current source document for image metadata
    // Match source by ID with the current media file
    const currentSource = useMemo(() => {
        const fileId = currentMedia?.file?.id;
        if (!fileId || sources.length === 0) {
            return null;
        }
        return sources.find((s) => s.id === fileId) ?? null;
    }, [sources, currentMedia?.file?.id]);

    const syncAnnotations = useCallback(
        async (
            annotations: Annotation[],
            syncOptions?: { aiToolUsed?: AiToolUsed; modelAssisted?: boolean }
        ): Promise<void> => {
            // Validate we have the required context
            if (!improveBatch) {
                const err = new Error("[useAnnotationSync] No improveBatch available");
                if (debug) console.warn(err.message);
                lastErrorRef.current = err;
                return;
            }

            if (!currentMedia?.file?.id) {
                const err = new Error("[useAnnotationSync] No current media file");
                if (debug) console.warn(err.message);
                lastErrorRef.current = err;
                return;
            }

            if (!currentSource) {
                const err = new Error("[useAnnotationSync] No current source document");
                if (debug) console.warn(err.message);
                lastErrorRef.current = err;
                return;
            }

            const { datasetId, annotationGroup } = improveBatch;
            const imageId = currentSource.id; // Use source.id, not file.id (which may be a UUID from MediaItem)
            const imageWidth = currentSource.width || 0;
            const imageHeight = currentSource.height || 0;

            // Validate required fields
            if (!datasetId) {
                const err = new Error(`[useAnnotationSync] Missing datasetId in improveBatch`);
                if (debug) console.warn(err.message, { improveBatch });
                lastErrorRef.current = err;
                return;
            }

            if (!annotationGroup) {
                const err = new Error(
                    `[useAnnotationSync] Missing annotationGroup in improveBatch`
                );
                if (debug) console.warn(err.message, { improveBatch });
                lastErrorRef.current = err;
                return;
            }

            if (!imageId) {
                const err = new Error(`[useAnnotationSync] Missing source id`);
                if (debug) console.warn(err.message, { currentSource });
                lastErrorRef.current = err;
                return;
            }

            if (!imageWidth || !imageHeight) {
                const err = new Error(
                    `[useAnnotationSync] Missing image dimensions for source ${imageId}`
                );
                if (debug) console.warn(err.message, { currentSource });
                lastErrorRef.current = err;
                return;
            }

            // Prevent concurrent syncs
            if (isSyncingRef.current) {
                if (debug) console.log("[useAnnotationSync] Sync already in progress, skipping");
                return;
            }

            isSyncingRef.current = true;
            lastErrorRef.current = null;

            try {
                const annotationData = buildAnnotationData(annotations, imageWidth, imageHeight);

                const nextAnnotationId = getNextAnnotationId(annotations);

                // Read pendingAiTool directly from store at sync time (not via reactive hook)
                // This prevents the callback from being recreated when pendingAiTool changes
                const storeState = useStore.getState();
                const pendingAiToolFromStore = storeState.getPendingAiTool();

                // Use explicit aiToolUsed if provided, otherwise use pending tool from store
                const aiToolToUse = syncOptions?.aiToolUsed ?? pendingAiToolFromStore;

                // Clear pending AI tool after using it
                if (pendingAiToolFromStore && !syncOptions?.aiToolUsed) {
                    storeState.clearPendingAiTool();
                }

                if (debug) {
                    console.log("[useAnnotationSync] Syncing annotations:", {
                        datasetId,
                        imageId,
                        annotationGroup,
                        boxCount: annotations.length,
                        aiToolUsed: aiToolToUse
                    });
                }

                const response = await api.datasets.updateAnnotation({
                    datasetId,
                    imageId,
                    group: annotationGroup,
                    annotation: annotationData,
                    nextAnnotationId,
                    expectedVersion: lastVersionRef.current,
                    aiToolUsed: aiToolToUse,
                    modelAssisted: syncOptions?.modelAssisted || !!aiToolToUse,
                    tabId: TAB_ID
                });

                if (response.error) {
                    throw new Error(response.error.message || "Failed to sync annotations");
                }

                // Update version for optimistic concurrency
                if (response.version !== undefined) {
                    lastVersionRef.current = response.version;
                }

                if (debug) {
                    console.log("[useAnnotationSync] Sync successful:", {
                        version: response.version,
                        updated: response.updated,
                        file: response.file
                    });
                }
            } catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                lastErrorRef.current = error;
                if (debug) {
                    console.error("[useAnnotationSync] Sync failed:", error);
                }
                throw error;
            } finally {
                isSyncingRef.current = false;
            }
        },
        [api, improveBatch, currentMedia, currentSource, debug]
    );

    // Queue sync function - adds to centralized queue instead of syncing immediately
    const queueSync = useCallback(
        (annotations: Annotation[]) => {
            // Validate we have the required context
            if (!improveBatch) {
                console.warn("[useAnnotationSync] Cannot queue sync: No improveBatch available");
                return;
            }

            if (!currentMedia?.file?.id) {
                console.warn("[useAnnotationSync] Cannot queue sync: No current media file");
                return;
            }

            if (!currentSource) {
                console.warn("[useAnnotationSync] Cannot queue sync: No current source document");
                return;
            }

            const { datasetId, annotationGroup } = improveBatch;
            const sourceId = currentSource.id;
            const imageWidth = currentSource.width || 0;
            const imageHeight = currentSource.height || 0;

            if (!datasetId || !annotationGroup || !imageWidth || !imageHeight) {
                console.warn("[useAnnotationSync] Cannot queue sync: Missing required fields");
                return;
            }

            // Read pending AI tool from store
            const storeState = useStore.getState();
            const pendingAiToolFromStore = storeState.getPendingAiTool();

            // Clear pending AI tool after capturing it
            if (pendingAiToolFromStore) {
                storeState.clearPendingAiTool();
            }

            const annotationData = buildAnnotationData(annotations, imageWidth, imageHeight);
            const nextAnnotationId = getNextAnnotationId(annotations);

            const request: SyncRequest = {
                sourceId,
                annotations,
                annotationData,
                datasetId,
                annotationGroup,
                nextAnnotationId,
                aiToolUsed: pendingAiToolFromStore,
                timestamp: Date.now()
            };

            annotationSyncQueue.queueSync(request);
        },
        [improveBatch, currentMedia?.file?.id, currentSource]
    );

    // Register the sync executor with the queue
    // This allows the queue to actually perform syncs using our API
    useEffect(() => {
        const executor = async (request: SyncRequest) => {
            console.log("[useAnnotationSync] Executing queued sync for source:", request.sourceId);

            const response = await api.datasets.updateAnnotation({
                datasetId: request.datasetId,
                imageId: request.sourceId,
                group: request.annotationGroup,
                annotation: request.annotationData,
                nextAnnotationId: request.nextAnnotationId,
                expectedVersion: lastVersionRef.current,
                aiToolUsed: request.aiToolUsed,
                modelAssisted: !!request.aiToolUsed,
                tabId: TAB_ID
            });

            if (response.error) {
                throw new Error(response.error.message || "Failed to sync annotations");
            }

            // Update version for optimistic concurrency
            if (response.version !== undefined) {
                lastVersionRef.current = response.version;
            }

            console.log("[useAnnotationSync] Queued sync completed:", {
                sourceId: request.sourceId,
                version: response.version
            });
        };

        annotationSyncQueue.setSyncExecutor(executor);
    }, [api]);

    // Note: setPendingAiTool and clearPendingAiTool are now provided via the Zustand store
    // They are exported from @/store as useSetPendingAiTool and useClearPendingAiTool
    // We keep the interface consistent for backwards compatibility but they're now no-ops here
    // since GeneratePredictions sets the tool via the store directly

    return {
        syncAnnotations,
        queueSync,
        get isSyncing() {
            return isSyncingRef.current;
        },
        get lastError() {
            return lastErrorRef.current;
        },
        get lastVersion() {
            return lastVersionRef.current;
        },
        // These are deprecated - use useSetPendingAiTool/useClearPendingAiTool from store instead
        setPendingAiTool: () => {
            console.warn(
                "[useAnnotationSync] setPendingAiTool is deprecated. Use useSetPendingAiTool from @/store instead"
            );
        },
        clearPendingAiTool: () => {
            console.warn(
                "[useAnnotationSync] clearPendingAiTool is deprecated. Use useClearPendingAiTool from @/store instead"
            );
        }
    };
}

export default useAnnotationSync;

// Re-export AiTools enum for convenience
export { AiTools } from "@rapid/utils/api/datasets";
